import { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ShoppingCart,
  CreditCard,
  FileText,
  Building2,
  Plus,
  Minus,
  Trash2,
  ArrowLeft,
  ArrowRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Receipt,
  Send,
  Lock,
  Shield,
  Wallet,
  Clock,
  X,
  Download,
  Globe,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default pricing (will be overridden by region)
const DEFAULT_PRICE_PER_UNIT = 35;
const ANNUAL_MONTHS = 12;

export default function BuildingPayments() {
  const { user } = useAuth();
  const { region, formatPrice, calculateSubscriptionPrice, paymentGateway, isInternational } = useRegion();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [pendingBuildings, setPendingBuildings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('cart');
  const [paypalLoading, setPaypalLoading] = useState(false);
  
  // Dialog states
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showAddToCartDialog, setShowAddToCartDialog] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Invoice form
  const [invoiceForm, setInvoiceForm] = useState({
    accounts_manager_email: '',
    accounts_manager_name: '',
    notes: ''
  });
  
  // Calculate price using region
  const calculatePrice = (unitCount) => {
    if (!region) return DEFAULT_PRICE_PER_UNIT * unitCount * ANNUAL_MONTHS;
    return region.pricePerUnit * unitCount * ANNUAL_MONTHS;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cartRes, invoicesRes] = await Promise.all([
        fetch(`${API_URL}/api/building-payments/cart`, { credentials: 'include' }),
        fetch(`${API_URL}/api/building-payments/invoices/my`, { credentials: 'include' })
      ]);

      if (cartRes.ok) {
        setCart(await cartRes.json());
      }
      
      if (invoicesRes.ok) {
        setInvoices(await invoicesRes.json());
      }

      // Get pending buildings (buildings without active payment)
      const buildingsRes = await fetch(`${API_URL}/api/agent-company/my-dashboard`, { credentials: 'include' });
      if (buildingsRes.ok) {
        const data = await buildingsRes.json();
        const pending = (data.buildings || []).filter(b => 
          b.payment_status !== 'paid' && b.status !== 'active'
        );
        setPendingBuildings(pending);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedBuilding) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/building-payments/cart/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          building_id: selectedBuilding.hoa_id,
          building_name: selectedBuilding.name,
          subscription_type: 'annual',  // Only annual subscriptions
          unit_count: selectedBuilding.stats?.total_units || selectedBuilding.total_units || 1
        })
      });

      if (response.ok) {
        toast.success('Added to cart');
        setShowAddToCartDialog(false);
        setSelectedBuilding(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add to cart');
      }
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveFromCart = async (buildingId) => {
    try {
      const response = await fetch(`${API_URL}/api/building-payments/cart/remove/${buildingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Removed from cart');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handlePayFastCheckout = async () => {
    if (cart.items.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/building-payments/checkout/payfast`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to PayFast
        window.location.href = data.redirect_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Checkout failed');
      }
    } catch (error) {
      toast.error('Checkout failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayPalCheckout = async () => {
    if (cart.items.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    setPaypalLoading(true);
    try {
      // For each building in cart, create PayPal order
      // For simplicity, we'll aggregate all buildings into one order
      const firstItem = cart.items[0];
      const totalUnits = cart.items.reduce((sum, item) => sum + (item.total_units || 1), 0);
      
      const response = await fetch(`${API_URL}/api/paypal/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          hoa_id: firstItem.building_id,
          building_name: cart.items.length > 1 
            ? `${cart.items.length} Buildings` 
            : firstItem.building_name,
          total_units: totalUnits,
          months: 12,
          return_url: `${window.location.origin}/payment/success?gateway=paypal`,
          cancel_url: `${window.location.origin}/agent/building-payments?cancelled=true`
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to PayPal approval URL
        window.location.href = data.approval_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'PayPal checkout failed');
      }
    } catch (error) {
      console.error('PayPal checkout error:', error);
      toast.error('PayPal checkout failed');
    } finally {
      setPaypalLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (cart.items.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (!invoiceForm.accounts_manager_email) {
      toast.error('Please enter accounts manager email');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/building-payments/invoice/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          building_ids: cart.items.map(i => i.building_id),
          ...invoiceForm
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Invoice ${data.invoice_number} generated`);
        setShowInvoiceDialog(false);
        setInvoiceForm({ accounts_manager_email: '', accounts_manager_name: '', notes: '' });
        
        // Clear cart
        await fetch(`${API_URL}/api/building-payments/cart/clear`, {
          method: 'DELETE',
          credentials: 'include'
        });
        
        fetchData();
        setActiveTab('invoices');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate invoice');
      }
    } catch (error) {
      toast.error('Failed to generate invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!region) return `R${amount?.toFixed(2) || '0.00'}`;
    return `${region.currencySymbol}${amount?.toFixed(2) || '0.00'}`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>,
      paid: <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Paid</Badge>,
      overdue: <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Overdue</Badge>,
    };
    return badges[status] || <Badge variant="secondary">{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="building-payments-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/agent')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Wallet className="w-8 h-8 text-indigo-600" />
                Building Payments
              </h1>
              <p className="text-slate-500 mt-1">
                Activate buildings with invoice or online payment
                {region && (
                  <span className="ml-2 inline-flex items-center gap-1 text-indigo-600">
                    • {region.currencySymbol}{region.pricePerUnit}/unit/month
                  </span>
                )}
              </p>
            </div>
            {region && (
              <Badge className="bg-slate-100 text-slate-700 flex items-center gap-1">
                <Globe className="w-3 h-3" />
                {region.flag} {region.name}
              </Badge>
            )}
          </div>
        </div>

        {/* Pending Buildings Alert */}
        {pendingBuildings.length > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">
                      {pendingBuildings.length} building(s) pending payment
                    </p>
                    <p className="text-sm text-amber-600">
                      Buildings remain locked until payment is complete
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  onClick={() => {
                    if (pendingBuildings[0]) {
                      setSelectedBuilding(pendingBuildings[0]);
                      setShowAddToCartDialog(true);
                    }
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Cart
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border mb-6">
            <TabsTrigger value="cart" className="gap-2">
              <ShoppingCart className="w-4 h-4" />
              Cart ({cart.items.length})
            </TabsTrigger>
            <TabsTrigger value="invoices" className="gap-2">
              <FileText className="w-4 h-4" />
              Invoices ({invoices.length})
            </TabsTrigger>
            <TabsTrigger value="pending" className="gap-2">
              <Building2 className="w-4 h-4" />
              Pending ({pendingBuildings.length})
            </TabsTrigger>
          </TabsList>

          {/* Cart Tab */}
          <TabsContent value="cart">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cart.items.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Your cart is empty</h3>
                      <p className="text-slate-500 mb-4">Add buildings to proceed with payment</p>
                      {pendingBuildings.length > 0 && (
                        <Button onClick={() => setActiveTab('pending')}>
                          View Pending Buildings
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  cart.items.map((item, idx) => (
                    <Card key={idx}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-6 h-6 text-indigo-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold">{item.building_name}</h3>
                              <p className="text-sm text-slate-500">
                                {item.subscription_type} subscription • {item.unit_count} units
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <p className="text-lg font-bold text-indigo-600">
                              {formatCurrency(item.price)}
                            </p>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleRemoveFromCart(item.building_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Order Summary */}
              <div>
                <Card className="sticky top-4">
                  <CardHeader>
                    <CardTitle>Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      {cart.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-slate-600 truncate max-w-[150px]">{item.building_name}</span>
                          <span>{formatCurrency(item.price)}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="border-t pt-4">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total</span>
                        <span className="text-indigo-600">{formatCurrency(cart.total)}</span>
                      </div>
                    </div>

                    {cart.items.length > 0 && (
                      <div className="space-y-3 pt-4">
                        {/* Region indicator */}
                        {region && (
                          <div className="flex items-center justify-center gap-2 text-sm text-slate-600 bg-slate-100 rounded-lg p-2 mb-2">
                            <Globe className="w-4 h-4" />
                            <span>{region.flag} {region.name} • {region.currency}</span>
                          </div>
                        )}
                        
                        {/* Payment Button - changes based on region */}
                        {isInternational ? (
                          <Button 
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            onClick={handlePayPalCheckout}
                            disabled={submitting || paypalLoading}
                          >
                            {submitting || paypalLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CreditCard className="w-4 h-4 mr-2" />
                                Pay Now with PayPal
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button 
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                            onClick={handlePayFastCheckout}
                            disabled={submitting}
                          >
                            {submitting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CreditCard className="w-4 h-4 mr-2" />
                                Pay Now with PayFast
                              </>
                            )}
                          </Button>
                        )}
                        
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-white px-2 text-slate-500">or</span>
                          </div>
                        </div>
                        
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={() => setShowInvoiceDialog(true)}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Request Invoice
                        </Button>
                        
                        <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                          <Shield className="w-3 h-3" />
                          <span>Secure {isInternational ? 'PayPal' : 'PayFast'} payment</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            {invoices.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No invoices yet</h3>
                  <p className="text-slate-500">Your invoices will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {invoices.map(invoice => (
                  <Card key={invoice.invoice_id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Receipt className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{invoice.invoice_number}</h3>
                            <p className="text-sm text-slate-500">
                              {invoice.buildings?.length || 0} building(s) • Due: {new Date(invoice.due_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {getStatusBadge(invoice.status)}
                          <p className="text-lg font-bold">{formatCurrency(invoice.total_amount)}</p>
                          <Button variant="outline" size="sm">
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                      
                      {invoice.status === 'pending' && (
                        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm font-medium mb-2">Banking Details:</p>
                          <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                            <span>Bank: {invoice.banking_details?.bank}</span>
                            <span>Account: {invoice.banking_details?.account_number}</span>
                            <span>Branch: {invoice.banking_details?.branch_code}</span>
                            <span>Reference: {invoice.banking_details?.reference}</span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Pending Buildings Tab */}
          <TabsContent value="pending">
            {pendingBuildings.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">All buildings are active!</h3>
                  <p className="text-slate-500">No pending payments</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingBuildings.map(building => (
                  <Card key={building.hoa_id} className="border-amber-200">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Lock className="w-5 h-5 text-amber-600" />
                        </div>
                        <Badge className="bg-amber-100 text-amber-700">Locked</Badge>
                      </div>
                      <h3 className="font-semibold mb-1">{building.name}</h3>
                      <p className="text-sm text-slate-500 mb-3">
                        {building.total_units || 0} units • {building.address || 'No address'}
                      </p>
                      <Button 
                        className="w-full"
                        onClick={() => {
                          setSelectedBuilding(building);
                          setShowAddToCartDialog(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add to Cart
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add to Cart Dialog */}
      <Dialog open={showAddToCartDialog} onOpenChange={setShowAddToCartDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Add Building to Cart
            </DialogTitle>
            <DialogDescription>
              Select subscription plan for {selectedBuilding?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="font-medium">{selectedBuilding?.name}</p>
              <p className="text-sm text-slate-500">{selectedBuilding?.stats?.total_units || selectedBuilding?.total_units || 0} units</p>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Annual Subscription Pricing</h4>
              <div className="text-sm text-blue-700 space-y-1">
                <p>{region?.currencySymbol || 'R'}{region?.pricePerUnit || 35} per unit x {selectedBuilding?.stats?.total_units || selectedBuilding?.total_units || 1} units x 12 months</p>
              </div>
            </div>

            <div className="p-3 bg-indigo-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-indigo-700">Annual Total</span>
                <span className="text-xl font-bold text-indigo-700">
                  {formatCurrency(calculatePrice(selectedBuilding?.stats?.total_units || selectedBuilding?.total_units || 1))}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddToCartDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddToCart} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add to Cart'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Request Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Request Invoice
            </DialogTitle>
            <DialogDescription>
              Invoice will be sent to your accounts department
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                {cart.items.length} building(s) • Total: <strong>{formatCurrency(cart.total)}</strong>
              </p>
            </div>

            <div>
              <Label>Accounts Manager Email *</Label>
              <Input
                type="email"
                value={invoiceForm.accounts_manager_email}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, accounts_manager_email: e.target.value })}
                placeholder="accounts@company.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Accounts Manager Name</Label>
              <Input
                value={invoiceForm.accounts_manager_name}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, accounts_manager_name: e.target.value })}
                placeholder="John Smith"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                placeholder="Any special instructions..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateInvoice} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Generate Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
